import theme from '@assets/theme';
import { useFetchJiraConfig } from '@http/queries/useFetchJiraConfig';
import { useFetchLicenses } from '@http/queries/useFetchLicenses';
import { useFetchTodos } from '@http/queries/useFetchTodos';
import { useFetchWhoami } from '@http/queries/useFetchWhoami';
import { useMyEffectiveRights } from '@http/queries/useMyEffectiveRights';
import { ThemeProvider, CssBaseline } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { fromUnixTime } from 'date-fns';
import { useAtom, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Outlet, useMatches, ScrollRestoration } from 'react-router-dom';
import { useBootScreen } from '@/hooks/useBootScreen';
import {
  demoModeAtom,
  effectiveAccessRightsAtom,
  tokenExpiresAtom,
  userAtom,
} from './global-store';
import clearStorageWithExceptions from './helpers/clearStorageWithExceptions';
import {
  clearPostLoginRedirect,
  readPostLoginRedirect,
  redirectToLogin,
  rememberPostLoginRedirect,
  resolvePostLoginTarget,
} from './helpers/postLoginRedirect';
import {
  LOGIN_PATH,
  hasPathPrefix,
  isAuthenticationPath,
} from './helpers/authenticationPaths';
import { resolveRouteAccessRedirect } from '@/guards/routeAccess';
import { MainLayout } from '@layout/MainLayout';
import { usePathname } from '@/hooks/usePathname';
import PrivacyDialog from './pages/authentication/PrivacyDialog';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { usePostHog } from '@posthog/react';
import {
  disablePostHogTracking,
  enablePostHogTracking,
  isPostHogTrackingEnabled,
} from '@/posthogClient';
import { Notification } from '@eye-able/ui';
import { useApplyBrandButtonVars } from '@/branding/useOrgBranding';
import { isUnauthenticatedError } from '@/http/authErrors';

function BaseRoute() {
  const posthog = usePostHog();
  const { t, i18n, ready: i18nReady } = useInlineTranslation();

  // Re-colours the shared yellow CTA button to the org brand colour, app-wide.
  useApplyBrandButtonVars();

  const queryClient = useQueryClient();

  const {
    isLoading: LicenseDataLoading,
    isError: LicenseDataError,
    error: licenseDataRequestError,
  } = useFetchLicenses();
  const {
    isLoading: whoamiLoading,
    isError: whoamiError,
    error: whoamiRequestError,
  } = useFetchWhoami();

  const { isLoading: todosLoading } = useFetchTodos();

  useFetchJiraConfig();

  // Current user's viewer/editor access levels. Intentionally NOT part of the
  // boot-readiness gate: edit gating is a UX nicety and the backend stays the
  // source of truth, so a slow/failed fetch must never block the app.
  const { data: myEffectiveRights } = useMyEffectiveRights();
  const setEffectiveAccessRights = useSetAtom(effectiveAccessRightsAtom);

  const [user, setUser] = useAtom(userAtom);
  const [tokenExpires, setTokenExpires] = useAtom(tokenExpiresAtom);
  const [demoMode] = useAtom(demoModeAtom);

  const matches = useMatches();
  const path = usePathname();
  const { enableBootScreen, disableBootScreen } = useBootScreen();

  const isUserReady =
    !whoamiLoading &&
    !whoamiError &&
    !LicenseDataLoading &&
    !todosLoading &&
    !!user;

  const isBootError = whoamiError || LicenseDataError;
  const isBooting = !isUserReady && !isBootError;

  const isPublicRoute =
    hasPathPrefix(path, '/privacy-policy') || isAuthenticationPath(path);
  const isSessionExpired =
    !isPublicRoute &&
    (isUnauthenticatedError(whoamiRequestError) ||
      isUnauthenticatedError(licenseDataRequestError));

  useEffect(() => {
    if (!isSessionExpired) return;

    disablePostHogTracking({ reset: true });
    Sentry.setUser(undefined);
    setUser(undefined);
    clearStorageWithExceptions();
    redirectToLogin();
  }, [isSessionExpired, setUser]);

  // The pending post-login target has served its purpose (or was never needed)
  // once the user is on a real page. Dropping it here keeps a stale deep link
  // from hijacking a later login in the same tab. Skipped while the session is
  // expiring, since the effect above has just stored this very page.
  useEffect(() => {
    if (isSessionExpired || !user || isAuthenticationPath(path)) return;

    clearPostLoginRedirect();
  }, [isSessionExpired, user, path]);

  useEffect(() => {
    if (isUserReady) return;

    disablePostHogTracking();
  }, [isUserReady]);

  useEffect(() => {
    if (!isUserReady) return;

    enablePostHogTracking();

    if (isPostHogTrackingEnabled()) {
      posthog.identify(user.id);
      posthog.setPersonProperties({
        organization: user.company,
        organization_id: user.primaryOrganisation,
        role: user.role,
        is_customer: user.isCustomer,
      });
    }

    Sentry.setUser({
      id: user.id,
      username: user.firstName?.[0] + '.' + user.lastName?.[0],
    });
  }, [posthog, isUserReady, user]);

  useEffect(() => {
    if (isUserReady) {
      posthog.capture('$pageview');
    }
  }, [posthog, isUserReady, path]);

  useEffect(() => {
    // Clear stale rights on logout/user change so the UI never gates edits
    // based on the previous user's access while the new fetch is pending.
    if (!user || myEffectiveRights?.userId !== user.id) {
      setEffectiveAccessRights(null);
      return;
    }
    setEffectiveAccessRights(myEffectiveRights.effectiveRights ?? null);
  }, [myEffectiveRights, user, setEffectiveAccessRights]);

  useEffect(() => {
    if (i18nReady && !isBooting) {
      disableBootScreen();
    } else {
      enableBootScreen();
    }
  }, [
    disableBootScreen,
    isBooting,
    demoMode,
    user,
    path,
    i18n.resolvedLanguage,
    queryClient,
    i18nReady,
    enableBootScreen,
  ]);

  useEffect(() => {
    if (!tokenExpires || !user) return;
    // show an alert before the token expires
    // TODO: show a dialog with relogin option
    const now = new Date();

    const expires = Number(tokenExpires);

    const expiresInSeconds = expires - (now.getTime() / 1000).toFixed(0);

    const expiresInMs = expiresInSeconds * 1000;

    const expireDate = fromUnixTime(Number(tokenExpires));

    // check if the token will expires in less than 24 days, which is the max value for setTimeout
    if (expiresInMs < 2147480000) {
      // logout the user when the token expires
      setTimeout(() => {
        setUser(undefined);
        setTokenExpires(undefined);
        alert(
          'Your session has expired, please login again on the following page',
        );
        redirectToLogin();
      }, expiresInMs);

      // show an alert 10 minutes before the token expires
      // TODO: show a dialog with relogin option
      setTimeout(() => {
        console.log('Token will expire in 10 minutes');
        // Set the minutes to 10 minutes before the token expires
        expireDate.setMinutes(expireDate.getMinutes() - 10);

        // Get hours and minutes, adding a leading zero if necessary
        const hours = expireDate.getHours().toString().padStart(2, '0');
        const minutes = expireDate.getMinutes().toString().padStart(2, '0');

        alert(
          `Your Session will expire in 10 minutes (at ${hours}:${minutes}), please logout and login again`,
        );
      }, expiresInMs - 6000);
    }
  }, [tokenExpires, user, setUser, setTokenExpires]);

  // Reload page if localStorage changes from another tab
  useEffect(() => {
    function reloadPage(event) {
      if (!document.hidden) return; // check if the tab is hidden

      const { key, oldValue, newValue } = event;

      if (
        ['reload', 'user', 'demoMode', 'X-Dev-Token', 'i18nextLng'].includes(
          key,
        ) &&
        newValue !== oldValue
      ) {
        window.location.reload();
      }
    }

    // this event only fires when the localStorage is changed from another tab
    window.addEventListener('storage', reloadPage);

    return () => {
      window.removeEventListener('storage', reloadPage);
    };
  }, []);

  if (!i18nReady || isBooting || isSessionExpired) {
    return null;
  }

  if (
    user &&
    !user.hasPassword &&
    path !== '/authentication/change-password' &&
    !demoMode &&
    !window.location.origin.includes('admin')
  ) {
    return window.location.assign('/authentication/change-password');
  } else {
    const redirectPath = resolveRouteAccessRedirect(user, matches, demoMode);

    // Signed out on a protected route: keep the link the visitor opened so that
    // login can return them to it. Stored here rather than in an effect because
    // the navigation below may leave the page before effects run.
    if (redirectPath === LOGIN_PATH) {
      rememberPostLoginRedirect();
    }

    const targetPath = resolvePostLoginTarget({
      redirectPath,
      pathname: path,
      pendingTarget: readPostLoginRedirect(),
    });

    if (targetPath && path !== targetPath) {
      return window.location.assign(targetPath);
    }
  }

  return (
    <ThemeProvider theme={theme} key={i18n.resolvedLanguage}>
      <CssBaseline />

      <ScrollRestoration />

      {!user || isAuthenticationPath(path) ? <Outlet /> : <MainLayout />}
      {path.includes('privacy-policy') ? null : <PrivacyDialog />}

      <Notification
        toastOptions={{
          closeButtonAriaLabel: t('close'),
        }}
        aria-live='polite'
      />
    </ThemeProvider>
  );
}

export default BaseRoute;
